/**
 * Top-level error boundary.
 *
 * Last-mile defense for the 6-canonical-error invariant. If any React render
 * throws (including a child component that managed to slip a raw exception
 * through), this boundary catches it and renders one of the canonical
 * messages via `toUserFacingError`. The user never sees a raw stack trace,
 * HTTP status, or `err.message`.
 */
import React from 'react';
import { Alert, AlertTitle, Box, Button, Container } from '@mui/material';
import { toUserFacingError } from '../utils/userFacingError';

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  // We deliberately do NOT store the raw error here for rendering — we only
  // store the canonical user-facing message resolved at catch time. The raw
  // error is logged to console.error for dev/DevTools visibility only.
  message: string;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, message: '' };
  }

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    return { hasError: true, message: toUserFacingError(error) };
  }

  componentDidCatch(error: unknown, info: React.ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] caught render error:', error, info);
  }

  handleReload = (): void => {
    window.location.reload();
  };

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <Container maxWidth="sm" sx={{ py: 8 }}>
          <Alert severity="error" sx={{ alignItems: 'flex-start' }}>
            <AlertTitle>Error</AlertTitle>
            <Box sx={{ mb: 2 }}>{this.state.message}</Box>
            <Button variant="contained" color="primary" onClick={this.handleReload}>
              Reload
            </Button>
          </Alert>
        </Container>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
